
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ScrutinyInput, 
  ScrutinyAnalysis, 
  ScrutinyDocument,
  ScrutinyExperience, 
  ScrutinyTeamSize, 
  ScrutinyTimeline, 
  ScrutinyAssets,
  FeatureType,
  BusinessIdea
} from '@/src/types';
import { useAuth } from '@/src/contexts/AuthContext';
import { useSearch } from '@/src/contexts/SearchContext';
import { generateScrutiny } from '@/src/services/apiServices';
import { validateScrutinyAssumptions } from '@/src/services/verificationService';
import { 
  db, 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  handleFirestoreError, 
  OperationType,
  getDoc
} from '../firebase';
import { COUNTRIES, SECTORS, CURRENCIES } from '@/src/constants';
import SuggestionDropdown from './SuggestionDropdown';
import DisclaimerTooltip from './DisclaimerTooltip';

interface ScrutinyDeckProps {
  onClose: () => void;
  onSelectIdea: (idea: BusinessIdea) => void;
}

const ScrutinyDeck: React.FC<ScrutinyDeckProps> = ({ onClose, onSelectIdea }) => {
  const { user, userProfile, isDemoGuest } = useAuth();
  const { state, dispatch, handleLocInputChange, handleSuggestionSelect } = useSearch();

  const renderKeys = new Set<string>();
  const validateAndGetUniqueKey = (id: string | number, idx: number, prefix: string): string => {
    let key = `${prefix}-${id}-${idx}`;
    if (renderKeys.has(key)) {
      console.warn(`[Key Validation] Duplicate key detected in ScrutinyDeck: ${key}. Resolving...`);
      let suffix = 1;
      while (renderKeys.has(`${key}-${suffix}`)) {
        suffix++;
      }
      key = `${key}-${suffix}`;
    }
    renderKeys.add(key);
    return key;
  };
  
  const [view, setView] = useState<'form' | 'loading' | 'results'>('form');
  const [formData, setFormData] = useState<ScrutinyInput>({
    ventureDescription: '',
    targetSector: '',
    targetLocation: '',
    estimatedCapital: 0,
    currency: state.preferredCurrency,
    experience: ScrutinyExperience.FirstTime,
    teamSize: ScrutinyTeamSize.Solo,
    launchTimeline: ScrutinyTimeline.Immediate,
    existingAssets: ScrutinyAssets.None
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ScrutinyInput, string>>>({});
  const [scrutinyResult, setScrutinyResult] = useState<ScrutinyDocument | null>(null);
  const [validatingAssumptionId, setValidatingAssumptionId] = useState<string | null>(null);
  const [validatedAssumptions, setValidatedAssumptions] = useState<Record<string, { narrative: string; hasContradiction: boolean }>>({});
  const [isOtherSector, setIsOtherSector] = useState(false);

  const validate = () => {
    const newErrors: Partial<Record<keyof ScrutinyInput, string>> = {};
    if (!formData.ventureDescription.trim()) newErrors.ventureDescription = 'Required';
    if (formData.ventureDescription.length > 1000) newErrors.ventureDescription = 'Max 1,000 characters';
    if (!formData.targetSector) newErrors.targetSector = 'Required';
    if (formData.targetSector === 'Others' && !formData.otherSector?.trim()) newErrors.otherSector = 'Required';
    if (!formData.targetLocation.trim()) newErrors.targetLocation = 'Required';
    if (formData.estimatedCapital <= 0) newErrors.estimatedCapital = 'Must be greater than 0';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!user) return;

    setView('loading');
    try {
      const usePro = ['optimize', 'team', 'enterprise'].includes(userProfile?.subscriptionTier);
      const analysis = await generateScrutiny(formData, usePro);
      
      const docData: ScrutinyDocument = {
        submittedAt: Date.now(),
        inputs: formData,
        analysis: analysis,
        userActions: {
          dismissed: false,
          savedToWarRoom: false
        }
      };

      if (isDemoGuest) {
        setScrutinyResult({ ...docData, id: 'guest_scrutiny_' + Math.random().toString(36).substr(2, 9) });
      } else {
        const scrutiniesRef = collection(db, 'users', user.uid, 'scrutinies');
        const docRef = await addDoc(scrutiniesRef, docData);
        setScrutinyResult({ ...docData, id: docRef.id });
      }
      setView('results');
    } catch (error) {
      console.error('Scrutiny failed:', error);
      setView('form');
      // Toast/Error handling
    }
  };

  const handleAction = async (action: 'launchpad' | 'discovery' | 'optimize' | 'war-room') => {
    if (!scrutinyResult || !user) return;

    try {
      const scrutinyId = scrutinyResult.id!;

      if (action === 'war-room') {
        if (!isDemoGuest) {
          const docRef = doc(db, 'users', user.uid, 'scrutinies', scrutinyId);
          await updateDoc(docRef, { 'userActions.savedToWarRoom': true });
        }
        setScrutinyResult(prev => prev ? { ...prev, userActions: { ...prev.userActions, savedToWarRoom: true } } : null);
        return;
      }

      if (!isDemoGuest) {
        const docRef = doc(db, 'users', user.uid, 'scrutinies', scrutinyId);
        await updateDoc(docRef, { 'userActions.sentToHub': action });
      }
      
      // Auto-route logic
      if (action === 'launchpad' || action === 'discovery') {
        const idea: BusinessIdea = {
          title: scrutinyResult.inputs.ventureDescription.slice(0, 40) + '...',
          description: scrutinyResult.inputs.ventureDescription,
          category: scrutinyResult.inputs.targetSector,
          locationName: scrutinyResult.inputs.targetLocation,
          estimatedStartupCost: `${scrutinyResult.inputs.estimatedCapital} ${scrutinyResult.inputs.currency}`,
          hubOrigin: action,
          scrutinyId: scrutinyId
        };
        onSelectIdea(idea);
      } else {
        dispatch({ type: 'SET_ACTIVE_FEATURE', payload: FeatureType.Optimize });
      }
      onClose();
    } catch (e) {
      if (!isDemoGuest) {
        handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}/scrutinies/${scrutinyResult.id}`);
      }
    }
  };

  const getFragilityColor = (index: number) => {
    if (index < 40) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (index < 70) return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-pure-red bg-pure-red/5 border-pure-red/20';
  };

  const getFragilityBarColor = (index: number) => {
    if (index < 40) return 'bg-emerald-500';
    if (index < 70) return 'bg-orange-500';
    return 'bg-pure-red';
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#322D29] flex flex-col overflow-hidden animate-in fade-in">
      {/* Header */}
      <div className="px-8 py-5 border-b border-white/[0.06] flex items-center justify-between bg-[#3a3430] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-9 h-9 rounded bg-[#F26522] flex items-center justify-center text-white text-base font-bold">
            <i className="fas fa-magnifying-glass"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-white font-heading leading-none">Scrutiny Deck</h2>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mt-1">Operational Stress-Testing Layer</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
        >
          <i className="fas fa-times text-lg"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pb-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
          
          <AnimatePresence mode="wait">
            {view === 'form' && (
              <motion.div 
                key="scrutiny-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                <div className="bg-[#3a3430] p-8 border border-white/[0.06] rounded-xl">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[#F26522] mb-6 flex items-center gap-2 font-heading">
                    <i className="fas fa-file-contract"></i>
                    Clinical Submission Form
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Venture Description */}
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Venture Description (Max 1,000 chars)</label>
                      <textarea 
                        className={`w-full h-36 px-4 py-3 bg-[#322D29] border ${errors.ventureDescription ? 'border-rose-500' : 'border-white/[0.08]'} text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all resize-none font-dm rounded-lg`}
                        placeholder="Describe your business model, target audience, and unique value proposition..."
                        value={formData.ventureDescription}
                        onChange={e => setFormData({ ...formData, ventureDescription: e.target.value.slice(0, 1000) })}
                      />
                      {errors.ventureDescription && <p className="text-[9px] font-bold uppercase text-rose-400">{errors.ventureDescription}</p>}
                    </div>

                    {/* Sector */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Target Sector</label>
                      <select 
                        className={`w-full px-4 py-3 bg-[#322D29] border ${errors.targetSector ? 'border-rose-500' : 'border-white/[0.08]'} text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg appearance-none cursor-pointer`}
                        value={formData.targetSector}
                        onChange={e => {
                          const val = e.target.value;
                          setIsOtherSector(val === 'Others');
                          setFormData({ ...formData, targetSector: val });
                        }}
                      >
                        <option value="" className="bg-[#322D29] text-white">Select Sector...</option>
                        {SECTORS.map((s, idx) => <option key={`scrutiny-sector-${s}-${idx}`} value={s} className="bg-[#322D29] text-white">{s}</option>)}
                        <option value="Others" className="bg-[#322D29] text-white">Others</option>
                      </select>
                      {isOtherSector && (
                        <input 
                          type="text"
                          placeholder="Specify Sector..."
                          className={`w-full px-4 py-3 mt-2 bg-[#322D29] border ${errors.otherSector ? 'border-rose-500' : 'border-white/[0.08]'} text-white font-normal text-xs focus:border-[#F26522] outline-none transition-all font-dm rounded-lg`}
                          value={formData.otherSector || ''}
                          onChange={e => setFormData({ ...formData, otherSector: e.target.value })}
                        />
                      )}
                    </div>

                    {/* Location */}
                    <div className="space-y-2 relative">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Target Location</label>
                      <div className="relative">
                        <input 
                          type="text"
                          placeholder="Address or Coordinates..."
                          className={`w-full px-4 py-3 bg-[#322D29] border ${errors.targetLocation ? 'border-rose-500' : 'border-white/[0.08]'} text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg pr-10`}
                          value={formData.targetLocation}
                          onChange={e => {
                            setFormData({ ...formData, targetLocation: e.target.value });
                            handleLocInputChange(e.target.value, 'launchpad');
                          }}
                        />
                        <button onClick={() => dispatch({ type: 'SET_MAP_PICKER', payload: { isOpen: true, target: 'launchpad' } })} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#F26522] transition-colors cursor-pointer">
                          <i className="fas fa-location-crosshairs"></i>
                        </button>
                      </div>
                      <SuggestionDropdown 
                        hub="launchpad" 
                        isFetchingSuggestions={state.isFetchingSuggestions}
                        locSuggestions={state.activeSuggestionHub === 'launchpad' ? state.locSuggestions : []}
                        onSelect={(name) => {
                          setFormData({ ...formData, targetLocation: name });
                          handleSuggestionSelect(name, 'launchpad');
                        }}
                        onClose={() => dispatch({ type: 'SET_SUGGESTIONS', payload: [] })}
                      />
                    </div>

                    {/* Capital */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Estimated Startup Capital</label>
                        <div className="flex gap-2">
                          <input 
                            type="number"
                            placeholder="Amount..."
                            className={`flex-1 px-4 py-3 bg-[#322D29] border ${errors.estimatedCapital ? 'border-rose-500' : 'border-white/[0.08]'} text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg`}
                            value={formData.estimatedCapital === 0 ? '' : formData.estimatedCapital}
                            onChange={e => setFormData({ ...formData, estimatedCapital: Number(e.target.value) })}
                          />
                          <select 
                            className="px-3 py-3 bg-[#322D29] border border-white/[0.08] text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg max-w-[130px] cursor-pointer"
                            value={formData.currency}
                            onChange={e => setFormData({ ...formData, currency: e.target.value })}
                          >
                            {CURRENCIES.map((curr, idx) => (
                              <option key={`scrutiny-curr-${curr.code}-${idx}`} value={curr.code} className="bg-[#322D29] text-white">
                                {curr.code} ({curr.symbol})
                              </option>
                            ))}
                          </select>
                        </div>
                    </div>

                    {/* Experience */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Founder Experience</label>
                      <select 
                        className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg appearance-none cursor-pointer"
                        value={formData.experience}
                        onChange={e => setFormData({ ...formData, experience: e.target.value as ScrutinyExperience })}
                      >
                        <option value={ScrutinyExperience.FirstTime} className="bg-[#322D29] text-white">First-time Founder</option>
                        <option value={ScrutinyExperience.SomeExperience} className="bg-[#322D29] text-white">Some Experience</option>
                        <option value={ScrutinyExperience.SerialFounder} className="bg-[#322D29] text-white">Serial Founder</option>
                      </select>
                    </div>

                    {/* Team Size */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Planned Team Size</label>
                        <select 
                          className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg appearance-none cursor-pointer"
                          value={formData.teamSize}
                          onChange={e => setFormData({ ...formData, teamSize: e.target.value as ScrutinyTeamSize })}
                        >
                          <option value={ScrutinyTeamSize.Solo} className="bg-[#322D29] text-white">Solo Founder</option>
                          <option value={ScrutinyTeamSize.Small} className="bg-[#322D29] text-white">Small (2-5)</option>
                          <option value={ScrutinyTeamSize.Medium} className="bg-[#322D29] text-white">Medium (6-15)</option>
                          <option value={ScrutinyTeamSize.Large} className="bg-[#322D29] text-white">Large (16+)</option>
                        </select>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Time to Launch</label>
                        <select 
                          className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg appearance-none cursor-pointer"
                          value={formData.launchTimeline}
                          onChange={e => setFormData({ ...formData, launchTimeline: e.target.value as ScrutinyTimeline })}
                        >
                          <option value={ScrutinyTimeline.Immediate} className="bg-[#322D29] text-white">Immediate (0-3 mo)</option>
                          <option value={ScrutinyTimeline.ShortTerm} className="bg-[#322D29] text-white">Short Term (3-6 mo)</option>
                          <option value={ScrutinyTimeline.MediumTerm} className="bg-[#322D29] text-white">Medium Term (6-12 mo)</option>
                          <option value={ScrutinyTimeline.LongTerm} className="bg-[#322D29] text-white">Long Term (12+ mo)</option>
                        </select>
                    </div>

                    {/* Assets */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Existing Assets</label>
                        <select 
                          className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white font-normal text-sm focus:border-[#F26522] outline-none transition-all font-dm rounded-lg appearance-none cursor-pointer"
                          value={formData.existingAssets}
                          onChange={e => setFormData({ ...formData, existingAssets: e.target.value as ScrutinyAssets })}
                        >
                          <option value={ScrutinyAssets.None} className="bg-[#322D29] text-white">None</option>
                          <option value={ScrutinyAssets.SomeCapital} className="bg-[#322D29] text-white">Some Capital</option>
                          <option value={ScrutinyAssets.Equipment} className="bg-[#322D29] text-white">Equipment</option>
                          <option value={ScrutinyAssets.CustomerBase} className="bg-[#322D29] text-white">Prior Customer Base</option>
                        </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center pt-4">
                  <button 
                    onClick={handleSubmit}
                    className="px-8 py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase tracking-wider text-xs rounded-lg transition-all flex items-center gap-3 font-heading cursor-pointer"
                  >
                    <span>Submit for Scrutiny</span>
                    <i className="fas fa-microscope text-sm"></i>
                  </button>
                </div>
              </motion.div>
            )}

            {view === 'loading' && (
              <motion.div 
                key="scrutiny-loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-28 space-y-6"
              >
                <div className="relative w-20 h-20">
                  <div className="absolute inset-0 border-3 border-[#F26522]/20 rounded-full"></div>
                  <div className="absolute inset-0 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="fas fa-microscope text-xl text-[#F26522] animate-pulse"></i>
                  </div>
                </div>
                <div className="text-center space-y-1.5">
                  <h3 className="text-lg font-bold uppercase tracking-wider text-white font-heading">Analysis in Progress</h3>
                  <p className="text-[10px] font-medium text-white/50 uppercase tracking-widest max-w-md">
                    Clinical dismantling of assumptions • Cross-referencing eNvesta AI intelligence layers
                  </p>
                </div>
                <div className="w-56 h-1 bg-white/10 overflow-hidden rounded-full relative">
                   <motion.div 
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 bg-[#F26522] rounded-full"
                   />
                </div>
              </motion.div>
            )}

            {view === 'results' && scrutinyResult && (
              <motion.div 
                key="scrutiny-results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-10"
              >
                {/* Fragility Section */}
                <div className="bg-[#3a3430] p-8 border border-white/[0.06] rounded-xl text-white relative overflow-hidden">
                  <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                      <div className="space-y-2">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F26522] font-heading">Composite Risk Model</span>
                        <h3 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight font-heading leading-none">Fragility Index</h3>
                      </div>
                      <div className="text-right">
                        <DisclaimerTooltip>
                          <span className="text-6xl font-black tracking-tighter leading-none">{scrutinyResult.analysis.fragilityIndex}</span>
                          <span className="text-xl font-bold text-white/40 ml-2">/ 100</span>
                        </DisclaimerTooltip>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${scrutinyResult.analysis.fragilityIndex}%` }}
                          transition={{ duration: 1.5, ease: 'easeOut' }}
                          className={`h-full rounded-full ${getFragilityBarColor(scrutinyResult.analysis.fragilityIndex)}`}
                        />
                      </div>
                      <p className="text-xs text-white/60 italic font-normal">
                        Interpretation: {scrutinyResult.analysis.fragilityIndex < 40 ? 'High baseline viability with manageable risk factors.' : scrutinyResult.analysis.fragilityIndex < 70 ? 'Significant structural gaps requiring immediate attention.' : 'Critical failure points identified. Concept requires fundamental reassessment.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Critical Gaps */}
                <div className="space-y-6">
                  <header className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2 font-heading">
                       <i className="fas fa-triangle-exclamation text-rose-400"></i>
                       Critical Gaps
                    </h3>
                    <span className="text-[9px] font-medium text-white/40 uppercase tracking-widest">Max 5 identified points</span>
                  </header>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {scrutinyResult.analysis.criticalGaps.map((gap, index) => {
                      const uniqueKey = validateAndGetUniqueKey(gap.id || 'gap', index, 'scrutiny-gap');
                      return (
                      <div key={uniqueKey} className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl hover:border-white/[0.15] transition-all flex flex-col gap-4">
                        <div className="flex items-center justify-between mb-1">
                           <span className="text-xs font-bold text-white uppercase tracking-wider font-heading">{gap.name}</span>
                           <span className={`text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${gap.severity === 'fatal' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : gap.severity === 'major' ? 'bg-[#F26522]/20 text-[#F26522] border-[#F26522]/30' : 'bg-white/5 text-white/60 border-white/10'}`}>
                             {gap.severity}
                           </span>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <span className="text-[8px] font-bold text-white/40 uppercase block mb-1">User Assumption</span>
                            <p className="text-xs text-white/70 italic font-normal">"{gap.userAssumption}"</p>
                          </div>
                          <div>
                            <span className="text-[8px] font-bold text-white/40 uppercase block mb-1">Market Conflict</span>
                            <p className="text-xs text-white/90 leading-relaxed font-medium">{gap.contradictingData}</p>
                            {gap.quantifiedGap && <p className="text-[10px] font-bold text-rose-400 mt-1 uppercase">Metric: {gap.quantifiedGap}</p>}
                          </div>
                        </div>
                        <div className="mt-auto pt-3 border-t border-white/[0.06] flex items-center justify-between">
                            <span className="text-[8px] font-medium text-white/40 uppercase tracking-widest">Source: {gap.source}</span>
                            <i className="fas fa-link text-[8px] text-white/30"></i>
                        </div>
                      </div>
                    ); })}
                  </div>
                </div>

                {/* Hidden Assumptions */}
                <div className="space-y-6">
                  <header className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2 font-heading">
                       <i className="fas fa-eye-slash text-white/40"></i>
                       Implicit Biases Flagged
                    </h3>
                  </header>

                  <div className="space-y-4">
                    {scrutinyResult.analysis.hiddenAssumptions.map((assumption, index) => {
                      const uniqueKey = validateAndGetUniqueKey(assumption.id || 'assumption', index, 'scrutiny-assumption');
                      return (
                      <div key={uniqueKey} className="p-6 bg-[#3a3430] border-l-4 border-[#F26522] border-y border-r border-white/[0.06] rounded-r-xl flex flex-col md:flex-row gap-6">
                        <div className="flex-1 space-y-1.5">
                           <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest block">Implicit Belief</span>
                           <h4 className="text-xs font-bold text-white uppercase tracking-wider font-heading">{assumption.assumption}</h4>
                        </div>
                        <div className="flex-[2] space-y-2">
                           <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest block">Why Unsupported</span>
                           <p className="text-xs text-white/70 font-normal">{assumption.whyUnsupported}</p>
                           <p className="text-[10px] font-medium text-white/90 mt-1 uppercase">Benchmarking: {assumption.relevantData}</p>
                           
                           {/* Brave Search validation indicator */}
                           <div className="mt-4 pt-3 border-t border-white/[0.06]">
                             {validatedAssumptions[assumption.id] ? (
                               <div className="space-y-2">
                                 <div className="flex items-center gap-2">
                                   <span className={`px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider rounded ${validatedAssumptions[assumption.id].hasContradiction ? 'bg-[#F26522]/20 text-[#F26522] border border-[#F26522]/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}`}>
                                     {validatedAssumptions[assumption.id].hasContradiction ? '● Analysis: Provider Discrepancy Found' : '● Analysis: Live Gap Verified'}
                                   </span>
                                 </div>
                                 <p className="text-xs text-white/60 italic leading-relaxed">
                                   {validatedAssumptions[assumption.id].narrative}
                                 </p>
                                </div>
                             ) : (
                               <button
                                 onClick={async (e) => {
                                   e.stopPropagation();
                                   if (!user) return;
                                   setValidatingAssumptionId(assumption.id);
                                   try {
                                     const res = await validateScrutinyAssumptions({
                                       title: scrutinyResult.inputs.targetSector || 'this sector',
                                       assumptionGap: assumption.assumption,
                                       city: scrutinyResult.inputs.targetLocation || 'Kampala'
                                     }, user.uid);
                                     setValidatedAssumptions(prev => ({ ...prev, [assumption.id]: res }));
                                   } catch (err) {
                                     console.error(err);
                                   } finally {
                                     setValidatingAssumptionId(null);
                                   }
                                 }}
                                 disabled={validatingAssumptionId !== null}
                                 className="px-3 py-1.5 bg-white/5 hover:bg-[#F26522] text-white/80 hover:text-white border border-white/10 rounded transition-colors text-[9px] font-bold uppercase tracking-wider flex items-center gap-2 cursor-pointer disabled:opacity-50"
                               >
                                 {validatingAssumptionId === assumption.id ? (
                                   <>
                                     <i className="fas fa-circle-notch animate-spin"></i>
                                     Checking Live Web Index...
                                   </>
                                 ) : (
                                   <>
                                     <i className="fas fa-brain"></i>
                                     Validate Assumption on Live Web
                                   </>
                                 )}
                               </button>
                             )}
                           </div>
                        </div>
                      </div>
                    ); })}
                  </div>
                </div>

                {/* Viable Pivots */}
                <div className="space-y-6">
                  <header className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2 font-heading">
                       <i className="fas fa-shuffle text-[#F26522]"></i>
                       Recommended Pivots
                    </h3>
                  </header>

                  <div className="grid grid-cols-1 gap-4">
                    {scrutinyResult.analysis.viablePivots.map((pivot, index) => {
                      const uniqueKey = validateAndGetUniqueKey(pivot.id || 'pivot', index, 'scrutiny-pivot');
                      return (
                      <div key={uniqueKey} className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl hover:border-white/[0.15] transition-all flex flex-col md:flex-row gap-6 relative overflow-hidden">
                        <div className="flex-1 space-y-3">
                           <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded bg-[#F26522] flex items-center justify-center text-white text-xs font-bold">
                               {pivot.confidenceScore * 100}%
                             </div>
                             <span className="text-[10px] font-bold text-white uppercase tracking-wider font-heading">Confidence Score</span>
                           </div>
                           <p className="text-xs sm:text-sm font-normal text-white/80 leading-relaxed">{pivot.description}</p>
                           <div className="flex flex-wrap gap-2">
                             {pivot.eliminatesGaps.map((gapId, idx) => (
                               <span key={`fixes-gap-${gapId}-${idx}`} className="text-[8px] font-bold uppercase px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded">Fixes: {gapId}</span>
                             ))}
                           </div>
                        </div>
                        <div className="w-full md:w-60 shrink-0 flex flex-col gap-3">
                           <div className="p-3 bg-[#322D29] border border-white/[0.06] rounded-lg space-y-2">
                              <div className="flex justify-between items-center">
                                 <span className="text-[8px] font-bold text-white/50 uppercase">Est. Capital</span>
                                 <span className="text-[10px] font-bold text-white uppercase">{pivot.estimatedCapital}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                 <span className="text-[8px] font-bold text-white/50 uppercase">Timeline</span>
                                 <span className="text-[10px] font-bold text-white uppercase">{pivot.estimatedTimeline}</span>
                              </div>
                           </div>
                           <button 
                            onClick={() => handleAction('launchpad')}
                            className="w-full py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[9px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer font-heading"
                           >
                             Simulate Pivot
                           </button>
                        </div>
                      </div>
                    ); })}
                  </div>
                </div>

                {/* Final Recommendation */}
                <div className="p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl flex flex-col items-center text-center space-y-6">
                   <div className="w-16 h-16 bg-[#322D29] border border-white/[0.08] rounded-xl flex items-center justify-center text-[#F26522] text-2xl">
                     <i className="fas fa-route"></i>
                   </div>
                   <div className="space-y-3 max-w-2xl">
                      <span className="text-[10px] font-bold text-[#F26522] uppercase tracking-[0.3em] font-heading">Command Decision</span>
                      <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white font-heading">{scrutinyResult.analysis.recommendedNextStep.action}</h3>
                      <p className="text-sm font-normal text-white/70 italic leading-relaxed">
                        "{scrutinyResult.analysis.recommendedNextStep.rationale}"
                      </p>
                   </div>
                   <div className="flex flex-wrap justify-center gap-3">
                      <button 
                        onClick={() => handleAction(scrutinyResult.analysis.recommendedNextStep.hubDestination as any)}
                        className="px-6 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center gap-2 transition-all cursor-pointer font-heading"
                      >
                        Router to {scrutinyResult.analysis.recommendedNextStep.hubDestination === 'launchpad' ? 'Launchpad' : scrutinyResult.analysis.recommendedNextStep.hubDestination.charAt(0).toUpperCase() + scrutinyResult.analysis.recommendedNextStep.hubDestination.slice(1) + ' Hub'}
                        <i className="fas fa-external-link text-[8px]"></i>
                      </button>
                      <button 
                        onClick={() => handleAction('war-room')}
                        disabled={scrutinyResult.userActions.savedToWarRoom}
                        className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center gap-2 transition-all cursor-pointer font-heading disabled:opacity-50"
                      >
                         {scrutinyResult.userActions.savedToWarRoom ? 'Saved to War Room' : 'Save to War Room'}
                        <i className="fas fa-bookmark text-[8px]"></i>
                      </button>
                      <button className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center gap-2 transition-all cursor-pointer font-heading">
                        Export PDF Analysis
                        <i className="fas fa-file-pdf text-[8px]"></i>
                      </button>
                   </div>
                </div>

              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>
    </div>
  );
};

export default ScrutinyDeck;
